import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findIndex } from "lodash/fp";

import type { ResponseError } from "@app/common/api";
import type { ApiEntityAccessView } from "@app/common/types";
import type {
  ApiEntityAccessPatchBody,
  GeneralAccessLevel,
  GeneralAccessPattern,
  SessionACLResponse,
  SessionACLPatchRequest,
} from "@app/common/types";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { replaceAt, removeAt } from "@app/utils/arrays";

import api from "../api";

interface UseReportShareProps {
  sessionId: string;
  enabled?: boolean;
  refetchInterval?: number;
}

interface ModifyGeneralAccessProps {
  sessionId: string;
  generalAccess: GeneralAccessPattern;
  accessLevel: GeneralAccessLevel;
}

interface ModifyEntityAccessProps {
  sessionId: string;
  entity: ApiEntityAccessView;
}
type DeleteEntityAccessProps = ModifyEntityAccessProps;
interface AddEntityAccessProps {
  sessionId: string;
  entities: ApiEntityAccessPatchBody[];
}

export const DEFAULT_ACCESS: SessionACLResponse = {
  accessRequests: [],
  generalAccess: {
    accessPattern: "people_with_access",
    accessPatternOptions: ["people_with_access"],
    accessLevel: "viewer_full",
    requiredReadAccount: false,
  },
  entityAccess: [],
  currentUserAccessLevel: "forbidden",
};

const fetchAccessList = async (
  sessionId: string
): Promise<SessionACLResponse> => {
  try {
    return await api.access.getSessionACL(sessionId);
  } catch (error) {
    if (isResponseErrorWithStatus(error, 403)) {
      const response = error.response as Response;
      if (response.status === 403) {
        return DEFAULT_ACCESS;
      }
    }
    throw error;
  }
};

const modifyEntityAccess = async ({
  sessionId,
  entity,
}: ModifyEntityAccessProps) => {
  const patchBody: SessionACLPatchRequest = {
    entityAccess: [{ notifyUser: false, ...entity }],
  };
  return api.access.patchSessionACL(sessionId, patchBody);
};

const modifyGeneralAccess = async ({
  sessionId,
  generalAccess,
  accessLevel,
}: ModifyGeneralAccessProps) => {
  const patchBody: SessionACLPatchRequest = {
    generalAccess: {
      accessPattern: generalAccess,
      accessLevel,
      requiredReadAccount: false,
    },
  };
  return api.access.patchSessionACL(sessionId, patchBody);
};

const addEntitiesAccess = async ({
  sessionId,
  entities,
}: AddEntityAccessProps) => {
  return api.access.patchSessionACL(sessionId, {
    entityAccess: entities,
  });
};

const removeEntityAccess = async ({
  sessionId,
  entity,
}: DeleteEntityAccessProps) => {
  return api.access.deleteSessionACL(sessionId, {
    sessionId,
    entityId: entity.entityId,
    kind: entity.kind,
  });
};

export const getAccessQueryKey = (sessionId: string): string[] => [
  "reportAccessList",
  sessionId,
];

// TODO: ENG-7460 re-enable... enabled flag once we figure out timing issues
const useReportShare = ({
  enabled: _,
  sessionId,
  refetchInterval,
}: UseReportShareProps) => {
  const queryClient = useQueryClient();
  const queryKey = getAccessQueryKey(sessionId);

  const accessListQuery = useQuery({
    queryKey,
    queryFn: () => fetchAccessList(sessionId),
    enabled: Boolean(sessionId),
    refetchOnWindowFocus: true,
    refetchInterval,
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 &&
      (!error?.response?.status ||
        error.response.status < 400 ||
        error.response.status > 499),
  });

  const modifyAccessMutation = useMutation({
    mutationFn: modifyEntityAccess,
    onMutate: async (patchRequestBody) => {
      await queryClient.cancelQueries({ queryKey });
      const previousAccessList = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) => {
        const oldAccessList = old as SessionACLResponse;
        const oldEntityAccess = oldAccessList?.entityAccess ?? [];

        return {
          ...oldAccessList,
          entityAccess: replaceAt(
            oldEntityAccess,
            patchRequestBody.entity,
            findIndex(
              { entityId: patchRequestBody.entity.entityId },
              oldEntityAccess
            )
          ),
        };
      });

      return { previousAccessList };
    },
    onError: (err, patchRequestBody, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousAccessList);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const modifyGeneralAccessMutation = useMutation({
    mutationFn: modifyGeneralAccess,
    onMutate: async (patchRequestBody) => {
      await queryClient.cancelQueries({ queryKey });
      const previousAccessList = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) => {
        const oldAccessList = old as SessionACLResponse;
        return {
          ...oldAccessList,
          generalAccess: {
            accessPattern: patchRequestBody.generalAccess,
            accessLevel: patchRequestBody.accessLevel,
            requiredReadAccount: false,
          },
        };
      });
      return { previousAccessList };
    },
    onError: (err, patchRequestBody, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousAccessList);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeAccessMutation = useMutation({
    mutationFn: removeEntityAccess,
    onMutate: async (deleteRequestBody) => {
      await queryClient.cancelQueries({ queryKey });
      const previousAccessList = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) => {
        const oldAccessList = old as SessionACLResponse;
        const oldEntityAccess = oldAccessList?.entityAccess ?? [];

        return {
          ...oldAccessList,
          entityAccess: removeAt(
            oldEntityAccess,
            findIndex(
              { entityId: deleteRequestBody.entity.entityId },
              oldEntityAccess
            )
          ),
        };
      });
      return { previousAccessList };
    },
    onError: (err, patchRequestBody, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousAccessList);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const addAccessMutation = useMutation({
    mutationFn: addEntitiesAccess,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    accessListQuery,
    accessList: accessListQuery.data ?? DEFAULT_ACCESS,
    modifyAccessMutation,
    modifyGeneralAccessMutation,
    removeAccessMutation,
    addAccessMutation,
  };
};

export default useReportShare;
